import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, PaperPlaneTilt } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Claimed-player-only dialog. Submits a reschedule request for an upcoming
 * match — admin must approve & move the slot.
 */
export default function RequestRescheduleDialog({ open, onOpenChange, match, onSubmitted }) {
  const [reason, setReason] = useState("");
  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Please provide a reason"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/matches/${match.id}/reschedule-request`,
        { reason: reason.trim(), preferred_date: preferredDate || null, preferred_time: preferredTime || null },
        { withCredentials: true });
      toast.success("Request sent to admin");
      onSubmitted?.();
      setReason(""); setPreferredDate(""); setPreferredTime("");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit request");
    } finally { setSubmitting(false); }
  };

  if (!match) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="request-reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" weight="fill" />
            Request Reschedule
          </DialogTitle>
          <DialogDescription>
            <strong>{match.player1_name || "P1"}</strong> vs <strong>{match.player2_name || "P2"}</strong>
            <br />
            Currently: {match.scheduled_date || "TBD"}
            {match.scheduled_time ? ` at ${match.scheduled_time}` : ""}
            {match.table_name ? ` · ${match.table_name}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Reason *</Label>
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value.slice(0, 500))}
              placeholder="e.g. Work commitment that evening, out of town, opponent availability…"
              rows={3}
              data-testid="reschedule-reason-input"
            />
            <p className="text-[10px] text-muted-foreground text-right">{reason.length}/500</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Preferred date (optional)</Label>
              <Input type="date" value={preferredDate} onChange={e => setPreferredDate(e.target.value)}
                data-testid="reschedule-preferred-date" />
            </div>
            <div>
              <Label className="text-xs">Preferred time (optional)</Label>
              <Input type="time" value={preferredTime} onChange={e => setPreferredTime(e.target.value)}
                data-testid="reschedule-preferred-time" />
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground p-2 rounded bg-muted/20 border border-border">
            Admin will review and approve / reject. Your opponent will be notified as well.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !reason.trim()} data-testid="submit-reschedule-request">
            <PaperPlaneTilt className="w-4 h-4 mr-1" weight="fill" />
            {submitting ? "Sending..." : "Send Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
